import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType, CodeBlock, BlockType, ValidationMessage, ValidationMessageType,
    SettingOption,
    SettingState,
    AppBlockPort,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import Libraries from '../Libraries';
import FeatureUtils from '../FeatureUtils';

export default class CustomFeature extends BaseFeature {
    constructor() {
        super();
        this.name = 'Custom Feature';
        this.id = 'custom_feature';
        this.singleInstance = false;
        this.settings = this.settings.concat([
            {
                id: 'NAME',
                name: 'Name',
                settingType: SettingType.STRING,
                required: true,
                helpText: 'This is what will be displayed in the UI',
                default: 'Feature',
            },
            {
                id: 'FEATURE_ID',
                name: 'Feature ID',
                settingType: SettingType.STRING,
                required: true,
                helpText: 'This is the unique identifier for the feature, also used when being referenced as a dependency or sub array feature',
                default: 'custom_feature_id',
            },
            {
                id: 'VERSION',
                name: 'Version',
                settingType: SettingType.STRING,
                helpText: 'The version of the feature',
                required: true,
                default: '1.0.0',
            },
            {
                id: 'RUNTIMES',
                name: 'Runtimes',
                settingType: SettingType.MULTISELECT_OPTION,
                required: true,
                helpText: 'The runtimes that this feature is available for',
                default: 'tios',
                options: [
                    {
                        name: 'TiOS',
                        value: 'tios',
                    },
                    {
                        name: 'Micropython',
                        value: 'micropython',
                    },
                    {
                        name: 'Zephyr',
                        value: 'zephyr',
                    },
                ],
            },
            {
                id: 'TYPE',
                name: 'Feature Type',
                settingType: SettingType.OPTION,
                required: true,
                helpText: `
- Service is a feature that provides a service, such as modbus gateway.

- Peripheral is a feature that provides a peripheral, such as a sensor, actuator, or communication interface.

- Block is a feature that can be used in the AppBlocks Canvas.

- Array Sub Item is a feature that is used as a sub item in another Service type feature.
                `,
                default: 'service',
                options: [
                    {
                        name: 'Service',
                        value: 'service',
                    },
                    {
                        name: 'Peripheral',
                        value: 'peripheral',
                    },
                    {
                        name: 'Block',
                        value: 'block',
                    },
                    {
                        name: 'Array Sub Item',
                        value: 'array_item',
                    },
                ],
            },
            {
                id: 'REQUIREMENTS',
                name: 'Requirements',
                settingType: SettingType.MULTISELECT_OPTION,
                required: true,
                default: '',
                helpText: 'The requirements that this feature has, e.g. MQTT requires network',
                options: [
                    {
                        name: 'TiOS',
                        value: 'tios',
                    },
                    {
                        name: 'Network',
                        value: 'network',
                    },
                ],
            },
            {
                id: 'DEPENDENCIES',
                name: 'Dependencies',
                settingType: SettingType.MULTISELECT_OPTION,
                required: true,
                helpText: 'The features or libraries that this feature depends on',
                default: '',
                options: [],
            },
            {
                id: 'CAPABILITIES',
                name: 'Provided Capabilities',
                settingType: SettingType.MULTISELECT_OPTION,
                required: true,
                helpText: 'The capabilities that this feature provides, e.g. a cellular modem custom feature provides network',
                default: '',
                options: [],
            },
            {
                id: 'SETTINGS',
                name: 'Settings',
                settingType: SettingType.ARRAY,
                required: true,
                helpText: 'The settings that this feature has. These will show up in the UI for the user to configure',
                default: [],
                refId: 'custom_feature_setting',
            },
            {
                id: 'CODE_BLOCKS',
                name: 'Code Blocks',
                settingType: SettingType.ARRAY,
                helpText: 'The code blocks that this feature has. These will be used in the code generation',
                required: true,
                default: [],
                refId: 'custom_feature_code_block',
            },
            {
                id: 'VARIABLES',
                name: 'Variables',
                settingType: SettingType.ARRAY,
                required: true,
                helpText: 'Variables are passed as arguments to the code blocks. An example would be a sum variable that is passed from a math addition block',
                default: [],
                refId: 'custom_feature_variable',
            },
        ]);
    }

    validate(): ValidationMessage[] {
        const messages: ValidationMessage[] = [];
        const TYPE = this.getSettingValue('TYPE');
        const FEATURE_ID = this.getSettingValue('FEATURE_ID');
        if (TYPE === 'block') {
            if (FEATURE_ID.indexOf('node_custom_') !== 0) {
                messages.push({
                    featureId: this.getID(),
                    settingId: 'FEATURE_ID',
                    messageType: ValidationMessageType.ERROR,
                    message: 'Feature ID must start with node_custom_',
                });
            }
        } else if (FEATURE_ID.indexOf('custom_') !== 0) {
            messages.push({
                featureId: this.getID(),
                settingId: 'FEATURE_ID',
                messageType: ValidationMessageType.ERROR,
                message: 'Feature ID must start with custom_',
            });
        }
        const SETTINGS = this.getSettingValue('SETTINGS');
        const settingIds = SETTINGS.map((setting: any) => setting.getSettingValue('SETTING_ID'));
        for (let i = 0; i < settingIds.length; i++) {
            const setting = SETTINGS[i];
            const SETTING_ID = setting.getSettingValue('SETTING_ID');
            if (settingIds.indexOf(SETTING_ID) !== i) {
                messages.push({
                    featureId: setting.getID(),
                    settingId: 'SETTING_ID',
                    messageType: ValidationMessageType.ERROR,
                    message: `Setting ID ${SETTING_ID} is not unique`,
                });
            }
        }
        return messages;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        if (settingId === 'CAPABILITIES') {
            return PlatformUtils.getAllCapabilities().map((capability) => {
                return {
                    name: `${capability.name} (${capability.description})`,
                    value: capability.name,
                };
            });
        }
        if (settingId === 'DEPENDENCIES') {
            const features = [];
            const allFeatures = Libraries.getList();
            for (let i = 0; i < allFeatures.length; i++) {
                const featureItem = FeatureUtils.getCodeItem(allFeatures[i], this.project);
                if (featureItem) {
                    features.push({
                        name: `${allFeatures[i]} (${featureItem.name})`,
                        value: allFeatures[i],
                    });
                }
            }
            return features;
        }
        return super.getSettingOptions(settingId);
    }

    getState(settingId: string): SettingState {
        const TYPE = this.getSettingValue('TYPE');
        if (settingId === 'VARIABLES') {
            if (TYPE !== 'block') {
                return SettingState.HIDDEN;
            }
        }
        return super.getState(settingId);
    }
}
